import { DatePicker, Form, Modal, notification } from 'antd';
import moment, { Moment } from 'moment';
import { FC, useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { Patientportal_PortalUserMsg, useUpdatePortalUserMutation } from '@/generated/graphql';
import { Maybe } from 'graphql/jsutils/Maybe';
import { buildPortalUserUpdateInput } from '../../helpers/portal-user';
import { ScreenSizeContext } from '../../pages/_app';
import { AveliosButton } from '../common-layout/avelios-button';

interface ChangeBirthDateModalProps {
  currentUser: Maybe<Patientportal_PortalUserMsg> | undefined;
  currentUserLoading?: boolean;
  open: boolean;
  onClose: () => void;
}

export const ChangeBirthDateModal: FC<ChangeBirthDateModalProps> = ({
  currentUser,
  currentUserLoading = false,
  open,
  onClose,
}) => {
  const { formatMessage } = useIntl();
  const { isMobile } = useContext(ScreenSizeContext);

  const [dateOfBirth, setDateOfBirth] = useState<Moment | null>(null);

  useEffect(() => {
    setDateOfBirth(
      currentUser?.dateOfBirth?.seconds
        ? moment(new Date((currentUser.dateOfBirth.seconds as number) * 1000))
        : null,
    );
  }, [currentUser?.dateOfBirth?.seconds]);

  const [updatePortalUser, { loading: updateLoading }] = useUpdatePortalUserMutation({
    onCompleted: () => {
      notification.success({
        message: formatMessage({ id: 'change-birth-date.success' }),
      });
      onClose();
    },
    onError: () => {
      notification.error({
        message: formatMessage({ id: 'change-birth-date.error' }),
      });
    },
  });

  const loading = currentUserLoading || updateLoading;

  const handleSave = () => {
    if (!dateOfBirth) {
      return;
    }
    updatePortalUser({
      variables: {
        input: {
          ...buildPortalUserUpdateInput(currentUser),
          dateOfBirth: { seconds: dateOfBirth.unix() },
        },
      },
    });
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={
        <AveliosButton
          loading={loading}
          type="primary"
          onClick={handleSave}
          disabled={!dateOfBirth}
        >
          {formatMessage({ id: 'action.save' })}
        </AveliosButton>
      }
      closable={true}
      title={<h2>{formatMessage({ id: 'change-birth-date' })}</h2>}
    >
      <Form layout="vertical">
        <Form.Item label={formatMessage({ id: 'user.birthday' })}>
          <DatePicker
            size="large"
            format="DD.MM.YYYY"
            showToday={false}
            style={{ width: '100%' }}
            value={dateOfBirth}
            disabledDate={(date) => date.isAfter(moment())}
            onChange={setDateOfBirth}
            inputReadOnly={isMobile}
            disabled={loading}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
